import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { favoritosAPI } from '../services/api';
import { useAuth } from './AuthContext';

interface Vaga {
  id: number;
  slug?: string;
  titulo: string;
  profissao?: string;
  empresa: {
    id: number;
    nome: string;
    logo?: string;
  };
  valorServico: number;
  tipoPagamento: string;
  localizacao: string;
  descricao: string;
  requisitos: string[];
  dataPublicacao: string;
  status: string;
}

interface VagaSalva {
  id: number;
  vaga: Vaga;
  dataSalva: string;
}

interface FaveVagasContextData {
  vagasSalvas: VagaSalva[];
  loading: boolean;
  error: string;
  isVagaSalva: (vagaId: number) => boolean;
  salvarVaga: (vaga: Vaga) => Promise<void>;
  removerVaga: (vagaId: number) => Promise<void>;
  refreshVagasSalvas: () => Promise<void>;
  clearError: () => void;
}

const FaveVagasContext = createContext<FaveVagasContextData>({} as FaveVagasContextData);

interface FaveVagasProviderProps {
  children: ReactNode;
}

export const FaveVagasProvider = ({ children }: FaveVagasProviderProps) => {
  const { user } = useAuth();
  const [vagasSalvas, setVagasSalvas] = useState<VagaSalva[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Carregar vagas salvas quando usuário logar
  useEffect(() => {
    if (user?.tipo === 'profissional') {
      refreshVagasSalvas();
    } else {
      // Limpar vagas se não for profissional
      setVagasSalvas([]);
    }
  }, [user]);

  const refreshVagasSalvas = async () => {
    if (!user || user.tipo !== 'profissional') return;

    try {
      setLoading(true);
      setError('');
      
      const response = await favoritosAPI.listar();
      const vagasData = response.data.vagasSalvas || [];
      
      // Transformar os dados da API para o formato esperado
      const vagasFormatadas = vagasData.map((item: any) => ({
        id: item.id || Date.now() + Math.random(), // ID único da vaga salva
        vaga: {
          id: item.id,
          slug: item.slug,
          titulo: item.titulo,
          empresa: {
            id: item.empresa.id,
            nome: item.empresa.nome,
            logo: item.empresa.logo
          },
          salario: item.salario,
          localizacao: item.localizacao,
          tipo: item.tipo,
          experiencia: item.experiencia,
          descricao: item.descricao || '',
          requisitos: item.requisitos || [],
          dataPublicacao: item.dataPublicacao,
          status: item.status || 'ativa'
        },
        dataSalva: item.dataSalva
      }));
      
      setVagasSalvas(vagasFormatadas);
    } catch (err: any) {
      console.error('Erro ao carregar vagas salvas:', err);
      setError('Erro ao carregar vagas salvas');
      setVagasSalvas([]);
    } finally {
      setLoading(false);
    }
  };

  const isVagaSalva = (vagaId: number): boolean => {
    return vagasSalvas.some(item => item.vaga?.id === vagaId);
  };

  const salvarVaga = async (vaga: Vaga) => {
    if (!user || user.tipo !== 'profissional') {
      setError('Apenas profissionais podem salvar vagas');
      return;
    }

    try {
      setError('');
      
      // Verificar se já está salva
      if (isVagaSalva(vaga.id)) {
        setError('Esta vaga já está salva');
        return;
      }

      await favoritosAPI.salvar(vaga.id);
      
      // Adicionar à lista local
      const novaVagaSalva: VagaSalva = {
        id: Date.now(), // ID temporário
        vaga: vaga,
        dataSalva: new Date().toISOString()
      };
      
      setVagasSalvas(prev => [...prev, novaVagaSalva]);
      
    } catch (err: any) {
      console.error('Erro ao salvar vaga:', err);
      setError(err.response?.data?.message || 'Erro ao salvar vaga');
    }
  };

  const removerVaga = async (vagaId: number) => {
    if (!user || user.tipo !== 'profissional') {
      setError('Apenas profissionais podem remover vagas salvas');
      return;
    }

    try {
      setError('');
      
      await favoritosAPI.salvar(vagaId); // API funciona como toggle
      
      // Remover da lista local
      setVagasSalvas(prev => prev.filter(item => item.vaga?.id !== vagaId));
      
    } catch (err: any) {
      console.error('Erro ao remover vaga:', err);
      setError(err.response?.data?.message || 'Erro ao remover vaga');
    }
  };

  const clearError = () => {
    setError('');
  };

  const contextValue: FaveVagasContextData = {
    vagasSalvas,
    loading,
    error,
    isVagaSalva,
    salvarVaga,
    removerVaga,
    refreshVagasSalvas,
    clearError
  };

  return (
    <FaveVagasContext.Provider value={contextValue}>
      {children}
    </FaveVagasContext.Provider>
  );
};

export const useFaveVagas = () => {
  const context = useContext(FaveVagasContext);
  
  if (!context) {
    throw new Error('useFaveVagas deve ser usado dentro de um FaveVagasProvider');
  }
  
  return context;
};